/* The two decisions Google sign-in needs that are worth executing, kept out of the component.
 *
 * ⚠️ THEY LIVE HERE BECAUSE `SignIn.tsx` HAS NO RUNNER. Nothing in this repo renders a React
 * component under test; `npm test` executes the pure modules and the harness reads sources. A URL
 * assembled inside a component is the one fallible thing with nothing to execute it — which is how
 * two notes shipped rendering a lowercase first word, invisible in review and obvious on screen.
 *
 * ⚠️ AND THE FAILURE THESE GUARD IS SILENT BY NATURE. An OAuth round trip that goes wrong does not
 * throw: the browser leaves, comes back, and there is simply no session. `SignIn.tsx`'s own header
 * refused to ship a Google button for exactly that reason — a control on the first screen of the
 * product that fails without saying anything. `oauthErrorMessage` is what turns that into a
 * sentence, and it is the half that made the button shippable.
 */

/** The `location` fields these rules read. Narrowed so a test need not fake a whole `Location`. */
export type UrlParts = { origin: string; pathname: string };

/**
 * Where the provider hands control back.
 *
 * ⚠️ THE HASH IS STRIPPED, AND ON THIS APP THAT IS NOT TIDINESS. The router is a HASH router
 * (`App.tsx` parses `window.location.hash`), so a coach signing in from `#/client/abc` would hand
 * the provider a return URL whose fragment is a route. The fragment is also where the IMPLICIT
 * OAuth flow puts its access token — which is why `lib/supabase.ts` sets `flowType: "pkce"`, and
 * why this returns a bare origin + path: one stable URL, no fragment to collide with, and exactly
 * one thing to put on the project's redirect allow-list.
 *
 * ⚠️ THE QUERY IS STRIPPED TOO, AND THAT ONE IS A BUG THIS PREVENTS RATHER THAN A PREFERENCE. A
 * failed attempt comes back as `?error=...`; carrying that into the next attempt would re-show the
 * previous failure's message over a sign-in that had just succeeded.
 */
export function oauthRedirectTarget(location: UrlParts): string {
  return location.origin + location.pathname;
}

/**
 * A visible sentence for an OAuth failure that came back in the URL, or null when there was none.
 *
 * The provider's and Supabase's OWN words, never a re-wording — the same rule `SignIn.tsx` already
 * applies to a rejected password, and for the same reason: the server is the authority on what
 * went wrong, and paraphrasing it either leaks what it withheld or invents what it did not say.
 *
 * ⚠️ `error_description` ARRIVES FORM-ENCODED, so `+` means space and `%20` does too.
 * `URLSearchParams` decodes both; hand-splitting on `&` does neither, and the failure mode is a
 * coach reading `Unable+to+exchange+external+code` on the first screen of the product.
 */
export function oauthErrorMessage(search: string): string | null {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const description = params.get("error_description");
  if (description !== null && description.trim() !== "") return description;
  const code = params.get("error");
  if (code !== null && code.trim() !== "") {
    // No description to show, so the code is all there is. Said plainly rather than dressed up:
    // a coach cannot act on it, but they can read it back to whoever can.
    return `Google sign-in did not complete (${code}).`;
  }
  return null;
}
